/**
 * Sprint O1 — Production Board (item-centric).
 *
 * Mission: "GP debe ser un sistema pensado para imprentas y empresas de
 * rotulación, no un CRM genérico. Cada clic debe ahorrar tiempo de producción."
 *
 * Surfaces every OrderItem of every open order in one screen so the operator
 * sees at a glance:
 *   - which stage each item is in (per-item production_status)
 *   - who owns it (assigned_employee_id)
 *   - how much time is left (delivery_date countdown)
 *   - which blockers are red (art / payment / material / customer approval)
 *
 * Click on any card → opens the full order via OrderDetailsModal (1 click).
 * All mutations PATCH the item endpoints added in Sprint O1.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";
import { urgencyOf, formatDate } from "@/lib/dateUtils";
import CountdownBadge from "@/components/CountdownBadge";
import OrderDetailsModal from "@/components/OrderDetailsModal";
import ProductionItemCard from "./ProductionItemCard";

const STATUSES = [
  "pending",
  "design",
  "ready_for_production",
  "in_production",
  "ready_for_delivery",
  "delivered",
];

const BLOCKER_FILTERS = [
  "missing_art",
  "missing_payment",
  "missing_material",
  "missing_approval",
];

export default function ProductionBoardPanel({ onChanged, onOpenPricingRule }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [methods, setMethods] = useState([]);

  // Filters
  const [filterBlocker, setFilterBlocker] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterEmployee, setFilterEmployee] = useState("");
  const [filterMethod, setFilterMethod] = useState("");

  // Order details modal
  const [detailsOrder, setDetailsOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Sprint O3 — Group view toggle: "flat" (default cards grid) or "by-method"
  // (vertical lanes grouped by decoration_method_id).
  const [viewMode, setViewMode] = useState("flat");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterBlocker) params.blocker = filterBlocker;
      if (filterStatus) params.production_status = filterStatus;
      if (filterEmployee) params.employee_id = filterEmployee;
      if (filterMethod) params.decoration_method_id = filterMethod;
      const { data } = await axios.get(`${API}/production/items`, { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  }, [filterBlocker, filterStatus, filterEmployee, filterMethod]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    axios
      .get(`${API}/employees`)
      .then(({ data }) => setEmployees(Array.isArray(data) ? data : []))
      .catch(() => setEmployees([]));
    axios
      .get(`${API}/decoration-methods?include_inactive=false`)
      .then(({ data }) => setMethods(Array.isArray(data) ? data : []))
      .catch(() => setMethods([]));
  }, []);

  const employeesById = useMemo(() => {
    const m = {};
    employees.forEach((e) => (m[e.id] = e));
    return m;
  }, [employees]);

  const methodsById = useMemo(() => {
    const m = {};
    methods.forEach((x) => (m[x.id] = x));
    return m;
  }, [methods]);

  const resetFilters = () => {
    setFilterBlocker("");
    setFilterStatus("");
    setFilterEmployee("");
    setFilterMethod("");
  };

  // Sprint O3 — group items by decoration_method_id into vertical lanes.
  // Cards inside each lane keep the same component, so all inline actions
  // (status, assignee, blockers, open order) still work.
  const renderByMethod = () => {
    const groups = {};
    for (const it of items) {
      const k = it.decoration_method_id || "__none__";
      (groups[k] ||= []).push(it);
    }
    const keys = Object.keys(groups).sort((a, b) => {
      // Items without a method last; otherwise alphabetic by method name
      if (a === "__none__") return 1;
      if (b === "__none__") return -1;
      const na = (methodsById[a]?.name || "").toLowerCase();
      const nb = (methodsById[b]?.name || "").toLowerCase();
      return na.localeCompare(nb);
    });
    return (
      <div className="space-y-4">
        {keys.map((k) => (
          <section
            key={k}
            data-testid={DASHBOARD.productionGroupHeader(k)}
            className="border border-[var(--border)] bg-[var(--surface-alt)]"
          >
            <header className="px-3 py-2 border-b border-[var(--border)] flex justify-between items-center">
              <h3 className="font-display font-bold tracking-tight">
                {k === "__none__" ? "Sin método asignado" : methodsById[k]?.name || k}
              </h3>
              <span className="font-mono-tech text-[0.7rem] tracking-wider text-[var(--text-secondary)]">
                {groups[k].length} ítem{groups[k].length === 1 ? "" : "s"}
              </span>
            </header>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-3 bg-white">
              {groups[k].map((it) => (
                <ProductionItemCard
                  key={`${it.order_id}-${it.item_id}`}
                  item={it}
                  urgency={urgencyOf(it.delivery_date)}
                  employee={employeesById[it.assigned_employee_id]}
                  employees={employees}
                  method={methodsById[it.decoration_method_id]}
                  onOpenOrder={() => openOrder(it.order_id)}
                  onChangeStatus={(s) => handlePatchStatus(it.order_id, it.item_id, s)}
                  onToggleBlocker={(field, val) =>
                    handleToggleBlocker(it.order_id, it.item_id, field, val)
                  }
                  onAssign={(eid) => handleAssign(it.order_id, it.item_id, eid)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    );
  };

  const openOrder = async (orderId) => {
    try {
      const { data } = await axios.get(`${API}/orders`);
      const order = (data || []).find((o) => o.id === orderId);
      if (!order) {
        toast.error("Orden no encontrada");
        return;
      }
      setDetailsOrder(order);
      setDetailsOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const handlePatchStatus = async (orderId, itemId, newStatus) => {
    try {
      await axios.patch(
        `${API}/orders/${orderId}/items/${itemId}/production-status`,
        { production_status: newStatus }
      );
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const handleToggleBlocker = async (orderId, itemId, field, newVal) => {
    try {
      await axios.patch(`${API}/orders/${orderId}/items/${itemId}/blockers`, {
        [field]: newVal,
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const handleAssign = async (orderId, itemId, employeeId) => {
    try {
      await axios.patch(`${API}/orders/${orderId}/items/${itemId}/assign`, {
        assigned_employee_id: employeeId || null,
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-6" data-testid={DASHBOARD.productionBoard}>
      <header>
        <div className="label-overline mb-1">PRD / 00</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
          {t("production_title")}
        </h1>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          {t("production_subtitle")}
        </p>
      </header>

      {/* Filters */}
      <section className="bg-[var(--surface-alt)] border border-[var(--border)] p-3 sm:p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <FilterSelect
            label={t("production_filter_blocker")}
            value={filterBlocker}
            onChange={setFilterBlocker}
            testId={DASHBOARD.productionFilterBlocker}
            options={[
              { value: "", label: t("production_filter_all") },
              ...BLOCKER_FILTERS.map((b) => ({
                value: b,
                label: t(`production_blocker_${b}`),
              })),
            ]}
          />
          <FilterSelect
            label={t("production_filter_status")}
            value={filterStatus}
            onChange={setFilterStatus}
            testId={DASHBOARD.productionFilterStatus}
            options={[
              { value: "", label: t("production_filter_all") },
              ...STATUSES.map((s) => ({
                value: s,
                label: t(`production_status_${s}`),
              })),
            ]}
          />
          <FilterSelect
            label={t("production_filter_employee")}
            value={filterEmployee}
            onChange={setFilterEmployee}
            testId={DASHBOARD.productionFilterEmployee}
            options={[
              { value: "", label: t("production_filter_all") },
              ...employees.map((e) => ({ value: e.id, label: e.name })),
            ]}
          />
          <FilterSelect
            label={t("production_filter_method")}
            value={filterMethod}
            onChange={setFilterMethod}
            testId={DASHBOARD.productionFilterMethod}
            options={[
              { value: "", label: t("production_filter_all") },
              ...methods.map((m) => ({ value: m.id, label: m.name })),
            ]}
          />
          <div className="flex items-end">
            <button
              type="button"
              data-testid={DASHBOARD.productionFiltersReset}
              onClick={resetFilters}
              className="w-full border border-black px-3 py-2 font-display font-bold text-sm hover:bg-black hover:text-white transition-colors"
            >
              {t("production_filter_reset")}
            </button>
          </div>
        </div>
        <div className="mt-3 flex justify-between items-center gap-3">
          <div className="font-mono-tech text-[0.7rem] tracking-wider text-[var(--text-secondary)]">
            {t("production_count").replace("{n}", String(items.length))}
          </div>
          {/* Sprint O3 — view-mode toggle */}
          <div
            data-testid={DASHBOARD.productionViewMode}
            className="inline-flex border border-black"
            role="tablist"
          >
            <button
              type="button"
              onClick={() => setViewMode("flat")}
              className={`px-2 py-1 text-[0.7rem] font-mono-tech tracking-wider uppercase ${
                viewMode === "flat" ? "bg-black text-white" : "bg-white"
              }`}
            >
              Por ítem
            </button>
            <button
              type="button"
              onClick={() => setViewMode("by-method")}
              className={`px-2 py-1 text-[0.7rem] font-mono-tech tracking-wider uppercase border-l border-black ${
                viewMode === "by-method" ? "bg-black text-white" : "bg-white"
              }`}
            >
              Por método
            </button>
          </div>
        </div>
      </section>

      {/* Grid */}
      {loading ? (
        <div className="text-sm text-[var(--text-secondary)]">…</div>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-[var(--border)] p-12 text-center text-sm text-[var(--text-secondary)]">
          {t("production_empty")}
        </div>
      ) : viewMode === "by-method" ? (
        renderByMethod()
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {items.map((it) => (
            <ProductionItemCard
              key={`${it.order_id}-${it.item_id}`}
              item={it}
              urgency={urgencyOf(it.delivery_date)}
              employee={employeesById[it.assigned_employee_id]}
              employees={employees}
              method={methodsById[it.decoration_method_id]}
              onOpenOrder={() => openOrder(it.order_id)}
              onChangeStatus={(s) => handlePatchStatus(it.order_id, it.item_id, s)}
              onToggleBlocker={(field, val) =>
                handleToggleBlocker(it.order_id, it.item_id, field, val)
              }
              onAssign={(eid) => handleAssign(it.order_id, it.item_id, eid)}
            />
          ))}
        </div>
      )}

      <OrderDetailsModal
        order={detailsOrder}
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setDetailsOrder(null);
        }}
        employees={employees}
        onOpenPricingRule={onOpenPricingRule}
        onChanged={() => {
          load();
          onChanged?.();
        }}
      />
    </div>
  );
}

function FilterSelect({ label, value, onChange, testId, options }) {
  return (
    <label className="block">
      <span className="label-overline block mb-1.5">{label}</span>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-[var(--border)] bg-white px-3 py-2 text-sm font-mono-tech focus:outline-none focus:border-black"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
